/**
 * HEIC/HEIF detection and browser-side conversion to JPEG.
 *
 * iPhones (iOS 11+) and modern mirrorless cameras produce HEIC/HEIF files.
 * Only Safari can decode these natively; in other browsers we must convert
 * to JPEG before the image can render, be compressed, or round-trip through
 * the standard Cloudinary upload pipeline.
 *
 * The heic2any library is intentionally loaded via dynamic import so the
 * ~1MB WASM bundle never ships unless a user actually picks a HEIC file.
 */

/**
 * Accept string for <input type="file"> pickers that should allow HEIC/HEIF.
 *
 * `image/*` is listed first so native cameras still work as before on iOS.
 * The explicit MIME types + extensions are required because Chrome/Firefox
 * on Windows do not consider HEIC to match `image/*` in the file dialog.
 */
export const IMAGE_INPUT_ACCEPT =
  'image/*,image/heic,image/heif,image/heic-sequence,image/heif-sequence,.heic,.heif';

export function isHeicFile(file: File): boolean {
  const name = file.name.toLowerCase();
  const type = file.type.toLowerCase();
  return (
    type === 'image/heic' ||
    type === 'image/heif' ||
    type === 'image/heic-sequence' ||
    type === 'image/heif-sequence' ||
    name.endsWith('.heic') ||
    name.endsWith('.heif')
  );
}

/**
 * Convert a HEIC/HEIF File to a JPEG File.
 *
 * The returned File has `type: 'image/jpeg'` and a `.jpg` extension so it
 * behaves identically to any other JPEG upload downstream (canvas decoding,
 * Cloudinary transforms, <img> rendering in all browsers).
 *
 * Limitation: for `.heic-sequence` / `.heif-sequence` files (rare — burst
 * mode / live photos) we keep only the first frame. That matches how other
 * HEIC viewers behave and is sufficient for a still-image workflow.
 *
 * Throws a user-friendly Error if heic2any fails to load (WASM load failure,
 * offline, blocked by corporate proxy) or if the input is malformed / from an
 * unsupported encoder. The caller's existing upload catch block will surface
 * the message via `setProgressError`, so the user sees something actionable
 * instead of a raw WASM stack trace.
 */
export async function convertHeicToJpeg(file: File): Promise<File> {
  let heic2any: (args: { blob: Blob; toType?: string; quality?: number }) => Promise<Blob | Blob[]>;
  try {
    ({ default: heic2any } = await import('heic2any'));
  } catch (err) {
    throw new Error(
      "Couldn't load HEIC support. Check your connection and try again, or export the photo as JPEG.",
    );
  }

  let converted: Blob | Blob[];
  try {
    converted = await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: 0.92,
    });
  } catch (err) {
    throw new Error(
      "Couldn't read this HEIC file. It may be corrupted or from an unsupported device — try exporting as JPEG.",
    );
  }

  const blob = Array.isArray(converted) ? converted[0] : converted;
  const newName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
  return new File([blob], newName, {
    type: 'image/jpeg',
    lastModified: file.lastModified,
  });
}
